import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PromoCatalogue, PromoResult, PromoScenarioInput, Promotion } from '@exigo/shared';
import { formatMoney, promoScenarioDefaults, promoTermText, qtyLimitText } from '@exigo/shared';
import { Card } from '../../components/ui/Card';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { NumberInput } from '../../components/ui/TextInput';
import { Switch } from '../../components/ui/Switch';
import { usePromotionImportMutation, usePromotionsMutation } from '../../api/queries';
import { dollarsToCents } from '../../lib/money';
import type { ReferenceData } from '../../api/types';
import styles from './PromotionsCard.module.css';
import calcStyles from './CalculatorPage.module.css';

const DEBOUNCE_MS = 350;

interface PromotionsCardProps {
  reference: ReferenceData;
  value: PromoScenarioInput | null;
  onChange: (next: PromoScenarioInput) => void;
  /** The deal-level FX rate, so the band test can convert AUD prices. */
  fx: number;
  /** A CSV refresh, or null to use the catalogue that shipped with the guide. */
  catalogue: PromoCatalogue | null;
  onCatalogueChange: (next: PromoCatalogue | null) => void;
}

/**
 * CSP promotions — customer discounts, and what they cost Exigo.
 *
 * Every figure in this card comes from `POST /promotions/evaluate`. Nothing is
 * computed here, for the same reason the calculator computes nothing: the band
 * arithmetic reads MCI payout bands that never leave the server.
 */
export function PromotionsCard({
  reference,
  value,
  onChange,
  fx,
  catalogue: refreshed,
  onCatalogueChange,
}: PromotionsCardProps): React.JSX.Element {
  // The deal's FX rate is the source of truth, so it is folded in on every
  // render: changing the rate up in Deal context has to reach the band test
  // without the user retyping anything here.
  const input = useMemo<PromoScenarioInput>(
    () => ({ ...(value ?? promoScenarioDefaults), fx }),
    [value, fx],
  );

  const [result, setResult] = useState<PromoResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importNote, setImportNote] = useState<string | null>(null);
  const mutation = usePromotionsMutation();
  const importMutation = usePromotionImportMutation();
  const fileRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const mutate = mutation.mutate;
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      mutate(
        // Sent with every evaluate so the server prices against the same list the
        // table is showing. Omitted entirely when there has been no refresh.
        refreshed ? { input, catalogue: refreshed } : { input },
        { onSuccess: setResult },
      );
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutate identity changes every render
  }, [input, refreshed]);

  const catalogue = refreshed ?? reference.promoCatalogue;
  const promoById = useMemo(
    () => new Map(catalogue.promotions.map((p) => [p.id, p])),
    [catalogue.promotions],
  );

  /** What the user has typed, for binding the inputs without a server round-trip. */
  const enteredById = useMemo(
    () => new Map(input.lines.map((l) => [l.promotionId, l])),
    [input.lines],
  );

  const setLine = useCallback(
    (promotionId: string, patch: { qty?: number; priceCents?: number }): void => {
      const existing = input.lines.find((l) => l.promotionId === promotionId);
      const next = {
        promotionId,
        qty: patch.qty ?? existing?.qty ?? 0,
        priceCents: patch.priceCents ?? existing?.priceCents ?? 0,
      };
      const lines = existing
        ? input.lines.map((l) => (l.promotionId === promotionId ? next : l))
        : [...input.lines, next];
      // Rows back at zero are dropped rather than stored as empty entries.
      onChange({ ...input, lines: lines.filter((l) => l.qty > 0 || l.priceCents > 0) });
    },
    [input, onChange],
  );

  // Stable handlers taking the id, so a keystroke in one row does not hand all
  // 29 rows a new callback and re-render every one of them.
  const setQty = useCallback(
    (promotionId: string, qty: number) => { setLine(promotionId, { qty }); },
    [setLine],
  );
  const setPrice = useCallback(
    (promotionId: string, priceCents: number) => { setLine(promotionId, { priceCents }); },
    [setLine],
  );

  const cur = input.currency;

  /**
   * Reads the chosen file and sends its text to be parsed.
   *
   * Entered quantities and prices are cleared on success, as in the guide: the
   * promotion ids are composed from the offer's own fields, so a refresh can
   * retire the very rows those figures were entered against, and silently
   * re-attaching them to whatever now shares an id would misprice the deal.
   */
  async function handleFile(file: File): Promise<void> {
    setImportError(null);
    try {
      const text = await file.text();
      const asAt = new Date().toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
      const res = await importMutation.mutateAsync({ csv: text, asAt });
      onCatalogueChange(res.catalogue);
      onChange({ ...input, lines: [] });
      setImportNote(
        `Refreshed from ${file.name} — ${String(res.skuRowsRead)} SKU rows grouped into ${String(
          res.catalogue.promotions.length,
        )} promotions. Quantities cleared.`,
      );
    } catch (err) {
      setImportNote(null);
      setImportError(err instanceof Error ? err.message : 'That file could not be read.');
    }
  }

  const money = (cents: number): string => formatMoney(cents);

  return (
    <>
      <Card className={calcStyles.section}>
        <div className={styles.headRow}>
          <p className={calcStyles.sectionTitle} style={{ marginBottom: 0 }}>
            CSP promotions — customer discounts
          </p>
          <div className={styles.headActions}>
            <span className={styles.stamp}>
              Snapshot as at {refreshed ? 'refreshed ' : ''}
              {catalogue.asAt} &middot; {catalogue.promotions.length} promotions
            </span>
            <Button
              size="sm"
              onClick={() => { fileRef.current?.click(); }}
              disabled={importMutation.isPending}
            >
              {importMutation.isPending ? 'Reading…' : 'Refresh from Partner Center CSV'}
            </Button>
            {refreshed && (
              <Button
                size="sm"
                onClick={() => {
                  onCatalogueChange(null);
                  onChange({ ...input, lines: [] });
                  setImportNote(null);
                  setImportError(null);
                }}
              >
                Use guide snapshot
              </Button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className={styles.hiddenFile}
              aria-label="Partner Center Promotion List export"
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Reset first, so re-picking the same file fires change again.
                e.target.value = '';
                if (file) void handleFile(file);
              }}
            />
          </div>
        </div>

        {importError && <div className={styles.importError}>{importError}</div>}
        {importNote && <div className={styles.importNote}>{importNote}</div>}

        <p className={styles.lede}>
          A promotion cuts what the customer pays. An MCI incentive is what Microsoft pays Exigo.{' '}
          <b>They are never added together.</b> They meet in one place: engagements sized on annual
          contract value are measured on the <b>discounted</b> price, so a percentage discount can
          push a deal down a band and cost more in lost incentive than it saves. Engagements sized on
          seat count are untouched. Enter a quantity and your sell price against any promotion and
          each line prices itself. Quantity does two jobs: it sizes the deal and it is tested against
          each promotion&rsquo;s own minimum and maximum. Price only converts that into dollars —
          leave it blank and you still get the eligibility answer.
        </p>

        <div className={styles.controls}>
          <div className={`${calcStyles.field} ${styles.controlField}`}>
            <span className={calcStyles.fieldLabel}>Customer segment</span>
            <Select
              value={input.segmentFilter}
              onChange={(v) => {
                onChange({ ...input, segmentFilter: v as PromoScenarioInput['segmentFilter'] });
              }}
              options={[
                { value: 'commercial', label: 'Commercial' },
                { value: 'education', label: 'Education' },
                { value: 'nonprofit', label: 'Non-profit' },
                { value: 'all', label: 'Show all' },
              ]}
              ariaLabel="Customer segment"
            />
          </div>

          <div className={`${calcStyles.field} ${styles.controlField}`}>
            <span className={calcStyles.fieldLabel}>Prices entered in</span>
            <Select
              value={input.currency}
              onChange={(v) => {
                onChange({ ...input, currency: v as PromoScenarioInput['currency'] });
              }}
              options={[
                { value: 'AUD', label: 'AUD' },
                { value: 'USD', label: 'USD' },
              ]}
              ariaLabel="Prices entered in"
            />
            <span className={calcStyles.fieldHint}>Your sell price, not list.</span>
          </div>

          <div className={styles.controlActions}>
            {/* Spacer so these sit on the control line, level with the selects
                rather than with their labels. */}
            <span className={calcStyles.fieldLabel} aria-hidden="true">
              &nbsp;
            </span>
            <div className={styles.actionRow}>
              <Switch
                label="Hide expired"
                checked={input.hideExpired}
                onChange={(v) => {
                  onChange({ ...input, hideExpired: v });
                }}
              />
              <Button
                size="sm"
                onClick={() => {
                  onChange({ ...input, lines: [] });
                }}
              >
                Clear quantities
              </Button>
            </div>
          </div>
        </div>

        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.nameCol}>Promotion</th>
                <th className={styles.r}>Disc</th>
                <th>Status</th>
                <th className={styles.r}>
                  Qty<span className={styles.thsub}>checked against the limits</span>
                </th>
                <th className={styles.r}>
                  Price / seat / mo
                  <span className={styles.thsub}>your sell rate — for dollars only</span>
                </th>
                <th className={styles.r}>Gross / yr</th>
                <th className={styles.r}>Saving / yr</th>
                <th className={styles.r}>Net / yr</th>
                <th>MCI effect</th>
              </tr>
            </thead>
            <tbody>
              {result === null && (
                <tr>
                  <td colSpan={9} className={styles.empty}>
                    Pricing promotions…
                  </td>
                </tr>
              )}
              {result?.lines.length === 0 && (
                <tr>
                  <td colSpan={9} className={styles.empty}>
                    Nothing matches this segment. Switch to &ldquo;Show all&rdquo;, or un-hide
                    expired promotions.
                  </td>
                </tr>
              )}
              {result?.lines.map((line) => {
                const promotion = promoById.get(line.promotionId);
                if (!promotion) return null;
                const entered = enteredById.get(line.promotionId);
                return (
                  <PromoRow
                    key={line.promotionId}
                    promotion={promotion}
                    line={line}
                    // What the user has typed, straight from local state. Binding
                    // these to `line` instead made every keystroke wait for the
                    // debounce and the round-trip before the character stuck.
                    qty={entered?.qty ?? 0}
                    priceCents={entered?.priceCents ?? 0}
                    onQty={setQty}
                    onPrice={setPrice}
                  />
                );
              })}
            </tbody>
            {result && result.totals.activeCount > 0 && (
              <tfoot>
                <tr className={styles.tfoot}>
                  <td colSpan={5}>
                    Total across {result.totals.activeCount} promotion
                    {result.totals.activeCount === 1 ? '' : 's'} ({cur})
                  </td>
                  <td className={styles.r}>{money(result.totals.grossYr)}</td>
                  <td className={`${styles.r} ${styles.save}`}>{money(result.totals.saveYr)}</td>
                  <td className={styles.r}>{money(result.totals.netYr)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      <PromoImpactPanel result={result} currency={cur} />
    </>
  );
}

/**
 * One promotion row.
 *
 * `qty` and `priceCents` are what the user has typed and come from local state,
 * so the field responds to the keystroke immediately. Everything else — status,
 * the money columns, the MCI effect — comes from `line`, the server's answer,
 * and lags by the debounce. That split is deliberate: the figures must stay
 * server-authoritative, but the text you are typing must not wait for them.
 *
 * Memoized so typing in one row does not re-render the other 28.
 */
const PromoRow = memo(function PromoRow({
  promotion,
  line,
  qty,
  priceCents,
  onQty,
  onPrice,
}: {
  promotion: Promotion;
  line: PromoResult['lines'][number];
  qty: number;
  priceCents: number;
  onQty: (promotionId: string, qty: number) => void;
  onPrice: (promotionId: string, cents: number) => void;
}): React.JSX.Element {
  const money = (cents: number): string => formatMoney(cents);
  const dead = !line.status.ok;
  return (
    <tr className={dead ? styles.dead : undefined}>
      <td>
        {promotion.name}
        <span className={styles.psub}>
          {promoTermText(promotion.termMonths)} &middot;{' '}
          {qtyLimitText(promotion.qtyMin, promotion.qtyMax)}
          {promotion.segment !== 'commercial' ? ` · ${promotion.segment}` : ''}
        </span>
      </td>
      <td className={styles.r}>
        <b>{promotion.discountPct}%</b>
      </td>
      <td>
        <span className={`${styles.pstat} ${styles[`st_${line.status.kind}`] ?? ''}`}>
          {line.status.label}
        </span>
        <span className={styles.pdate}>{line.status.sub}</span>
      </td>
      <td className={styles.r}>
        <NumberInput
          value={qty}
          onValueChange={(v) => {
            onQty(promotion.id, Math.max(0, Math.round(v)));
          }}
          min={0}
          ariaLabel={`Quantity for ${promotion.name}`}
        />
      </td>
      <td className={styles.r}>
        <NumberInput
          value={priceCents / 100}
          onValueChange={(v) => {
            onPrice(promotion.id, dollarsToCents(v));
          }}
          min={0}
          step={0.01}
          ariaLabel={`Price per seat per month for ${promotion.name}`}
        />
      </td>
      <td className={styles.r}>{line.grossYr > 0 ? money(line.grossYr) : '—'}</td>
      {/* Saving and net stay blank unless the line actually counts: an
          out-of-range or expired row still shows a gross, but claiming a saving
          against something that cannot be transacted would be a fiction. */}
      <td className={styles.r}>{line.active ? money(line.saveYr) : '—'}</td>
      <td className={styles.r}>{line.active ? money(line.netYr) : '—'}</td>
      <td>
        <span
          className={`${styles.prisk} ${
            line.effectOverride ? styles.riskBad : (styles[`risk_${line.risk}`] ?? '')
          }`}
        >
          {line.effectLabel}
        </span>
      </td>
    </tr>
  );
});

function PromoImpactPanel({
  result,
  currency,
}: {
  result: PromoResult | null;
  currency: 'AUD' | 'USD';
}): React.JSX.Element | null {
  if (!result) return null;

  if (!result.impact) {
    return (
      <p className={styles.inote}>
        Enter a quantity and your sell price per seat per month against any promotion above. Each
        line shows the gross annual value, the discount and the net, and the last column tells you
        whether the discount costs Exigo anything in lost incentive.
      </p>
    );
  }

  const { impact } = result;
  const band = impact.band;

  return (
    <Card className={calcStyles.section}>
      <p className={calcStyles.sectionTitle}>What this costs and what it saves</p>

      <div className={styles.igrid}>
        <div className={styles.icell}>
          <p className={styles.ik}>Customer saves</p>
          <p className={`${styles.iv} ${styles.pos}`}>{formatMoney(impact.customerSavesYr)}</p>
          <p className={styles.is}>
            per year, {currency}, across {impact.activeCount} promotion
            {impact.activeCount === 1 ? '' : 's'}
          </p>
        </div>

        <div className={styles.icell}>
          <p className={styles.ik}>Customer pays</p>
          <p className={styles.iv}>{formatMoney(impact.customerPaysYr)}</p>
          <p className={styles.is}>instead of {formatMoney(impact.grossYr)} {currency}</p>
        </div>

        {band && (
          <>
            <div className={styles.icell}>
              <p className={styles.ik}>Business Processes band</p>
              <p className={`${styles.iv} ${band.lostUsd > 0 ? styles.neg : styles.pos}`}>
                {band.bandFrom ?? '—'} &rarr; {band.bandTo ?? '—'}
              </p>
              <p className={styles.is}>
                on {formatMoney(band.discountedAcvUsd)} USD discounted ACV
              </p>
            </div>
            <div className={styles.icell}>
              <p className={styles.ik}>Costs Exigo</p>
              <p className={`${styles.iv} ${band.lostUsd > 0 ? styles.neg : styles.pos}`}>
                {band.lostUsd > 0 ? `-${formatMoney(band.lostUsd)}` : formatMoney(0)}
              </p>
              <p className={styles.is}>
                {band.lostUsd > 0 ? 'USD of lost incentive' : 'band holds — discount is free'}
              </p>
            </div>
          </>
        )}

        {impact.bandNeedsFx && (
          <div className={styles.icell}>
            <p className={styles.ik}>Business Processes band</p>
            <p className={styles.iv}>—</p>
            <p className={styles.is}>
              Set the AUD per USD rate in Deal context to test the band, or switch prices to USD.
            </p>
          </div>
        )}

        {!band && !impact.bandNeedsFx && (
          <div className={styles.icell}>
            <p className={styles.ik}>Costs Exigo</p>
            <p className={`${styles.iv} ${styles.pos}`}>{formatMoney(0)}</p>
            <p className={styles.is}>nothing selected sizes an MCI band on price</p>
          </div>
        )}
      </div>

      <div className={styles.notes}>
        {impact.familyNotes.map((f) => (
          <p key={f.family} className={styles.inote}>
            <b>{f.name}</b> — {f.note}
          </p>
        ))}
      </div>

      {band && band.zones.length > 0 && (
        <div className={styles.zone}>
          <b>Band-drop zones for a {band.discountPct}% discount.</b> Gross annual contract value
          inside these ranges loses a band once discounted:{' '}
          {band.zones.map((z, i) => (
            <span key={z.fromUsd}>
              {i > 0 ? ' · ' : ''}
              {formatMoney(z.fromUsd)}–{formatMoney(z.toUsd)} loses {formatMoney(z.losesUsd)}
            </span>
          ))}{' '}
          (USD). Above each range the discount is free.
        </div>
      )}
    </Card>
  );
}
